import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api.js';
import type { FieldChange, SettingsGroup } from './settingsFormat.js';
import type { PlatformConfig } from './settingsTypes.js';

/** 356: the reads and the save behind the settings page (contracts/settings.md). */
export interface HistoryRecord {
  id: string;
  at: string;
  group: SettingsGroup;
  setting: string;
  changes: FieldChange[];
  actor: { kind: 'staff'; name: string | null } | { kind: 'system'; reason: string | null };
}

export interface HistoryPage {
  items: HistoryRecord[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export type LatestChanges = Partial<
  Record<string, { last: HistoryRecord; state_since: string | null }>
>;

export type HistoryFilter = { group?: SettingsGroup; system?: boolean };

export const CONFIG_QUERY = ['platform-config'] as const;
const LATEST_QUERY = ['platform-config', 'history', 'latest'] as const;

export function usePlatformConfig() {
  return useQuery({
    queryKey: CONFIG_QUERY,
    queryFn: () => api.get<PlatformConfig>('/platform-config'),
    // 063 (FR-010): a refusal is not a transient fault, and retrying it only delays the sentence
    // that explains it.
    retry: false,
  });
}

export function useLatestChanges(enabled: boolean) {
  return useQuery({
    queryKey: LATEST_QUERY,
    queryFn: () => api.get<LatestChanges>('/platform-config/history/latest'),
    enabled,
    retry: false,
  });
}

export function historyPath(filter: HistoryFilter, page: number): string {
  const q = new URLSearchParams();
  if (filter.group) q.set('group', filter.group);
  if (filter.system) q.set('actor', 'system');
  q.set('page', String(page));
  q.set('limit', '25');
  return `/platform-config/history?${q.toString()}`;
}

export function useSettingsHistory(filter: HistoryFilter, page: number) {
  return useQuery({
    queryKey: ['platform-config', 'history', filter.group ?? null, !!filter.system, page],
    queryFn: () => api.get<HistoryPage>(historyPath(filter, page)),
    retry: false,
  });
}

/** 389: the home pop-up's counts for its current version. */
export interface HomePopupStats {
  version: string | null;
  shown: number;
  posters: Array<{ id: string; seen: number; taps: number }>;
}

export function useHomePopupStats(enabled: boolean) {
  return useQuery({
    queryKey: ['platform-config', 'home-popup-stats'],
    queryFn: () => api.get<HomePopupStats>('/home-popup/stats'),
    enabled,
    retry: false,
  });
}

export function useSaveSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch<PlatformConfig>('/platform-config', body),
    onSuccess: async (updated) => {
      queryClient.setQueryData(CONFIG_QUERY, updated);
      await queryClient.invalidateQueries({ queryKey: ['platform-config'] });
    },
  });
}

export interface ImpactUnpaid {
  setting: 'unpaid_bookings';
  count: number;
  capped: boolean;
  notifies_customers: boolean;
}
export interface ImpactWorkflow {
  setting: 'auto_workflow';
  check_outs: number;
  closes: number;
  capped: boolean;
  notifies_customers: boolean;
}

/** 374: rentals whose photo window is open right now, not yet sent. */
export interface ImpactInspectionWindows {
  setting: 'inspection_windows';
  pickup_open: number;
  return_open: number;
  capped: boolean;
  notifies_customers: boolean;
}

export function impactPath(
  setting: 'unpaid_bookings' | 'auto_workflow' | 'inspection_windows',
  timers?: { unpaid_cancel_minutes: number; hold_minutes: number },
): string {
  if (setting === 'auto_workflow') return '/platform-config/impact?setting=auto_workflow';
  if (setting === 'inspection_windows') return '/platform-config/impact?setting=inspection_windows';
  const q = new URLSearchParams({
    setting,
    unpaid_cancel_minutes: String(timers?.unpaid_cancel_minutes ?? ''),
    hold_minutes: String(timers?.hold_minutes ?? ''),
  });
  return `/platform-config/impact?${q.toString()}`;
}
